"use client"

import React from "react"
import { AlertTriangle, RefreshCw } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"

interface ErrorPageProps {
  error: Error & { digest?: string }
  reset: () => void
}

export default function ErrorPage({ error, reset }: ErrorPageProps) {
  const retryRef = React.useRef<HTMLButtonElement>(null)

  // §07.8 "The retry control receives focus when a boundary catches, so
  // keyboard users are not stranded mid-page." The page boundary steals
  // focus by design (the section boundary does not).
  React.useEffect(() => {
    retryRef.current?.focus()
  }, [])

  React.useEffect(() => {
    // Report error to Sentry. The tag reads `RouteErrorBoundary`, not
    // `GlobalErrorBoundary` as it did before TES-1844: this file catches throws
    // from the route *below* the root layout, while `app/global-error.tsx`
    // catches throws from the layout itself and tags them
    // `RootLayoutErrorBoundary`. Two boundaries with two failure domains need
    // two names or triage attributes them to the wrong one.
    import('@sentry/browser').then(({ captureException }) => {
      captureException(error, { tags: { context: 'RouteErrorBoundary' } })
    }).catch(() => {})
  }, [error])

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--tls-canvas)] p-4">
      <Card className="max-w-lg w-full border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface)]">
        <CardHeader className="text-center">
          <div className="mx-auto mb-6 flex size-20 items-center justify-center rounded-[var(--radius-pill)] border border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface-elevated)]">
            <AlertTriangle className="size-10 text-[var(--tls-fg-strong)]" />
          </div>
          {/* §07.8: kicker (type-label) names the blast radius — this page,
              not the site. No error code: the digest goes to Sentry above,
              never to the reader. */}
          <p className="tls-eyebrow mb-2 [--tls-eyebrow-color:var(--tls-fg-subtle)]">
            Error on this page
          </p>
          <CardTitle className="mb-2 font-display text-2xl">
            We hit an unexpected error
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-center space-y-2">
            <p className="text-[var(--tls-fg-strong)]">
              Something interrupted the page before it could finish loading.
            </p>
            <p className="text-sm text-[var(--tls-fg-muted)]">
              Try the action again, or head back to the sound library and restart the install flow.
            </p>
          </div>
          
          {process.env.NODE_ENV === 'development' && (
            <details className="rounded-[var(--ls-radius-0)] border border-[color:var(--tls-border-strong)] bg-[var(--tls-surface-elevated)] p-4">
              <summary className="cursor-pointer font-bold text-[var(--tls-fg-strong)]">
                Developer Debug Info
              </summary>
              <div className="mt-3 space-y-2">
                <div>
                  <span className="text-[var(--tls-fg-muted)] text-xs font-mono">Message:</span>
                  <p className="text-[var(--tls-fg-strong)] text-sm font-mono bg-[color:color-mix(in_srgb,var(--tls-canvas)_36%,transparent)] p-2 rounded-[var(--ls-radius-0)] mt-1">
                    {error.message}
                  </p>
                </div>
                {error.digest && (
                  <div>
                    <span className="text-[var(--tls-fg-muted)] text-xs font-mono">Digest:</span>
                    <p className="text-[var(--tls-fg-strong)] text-sm font-mono bg-[color:color-mix(in_srgb,var(--tls-canvas)_36%,transparent)] p-2 rounded-[var(--ls-radius-0)] mt-1">
                      {error.digest}
                    </p>
                  </div>
                )}
                {error.stack && (
                  <div>
                    <span className="text-[var(--tls-fg-muted)] text-xs font-mono">Stack Trace:</span>
                    <pre className="text-[var(--tls-fg-muted)] text-xs font-mono bg-[color:color-mix(in_srgb,var(--tls-canvas)_36%,transparent)] p-2 rounded-[var(--ls-radius-0)] mt-1 overflow-auto max-h-40">
                      {error.stack}
                    </pre>
                  </div>
                )}
              </div>
            </details>
          )}
          
          <div className="flex gap-3 justify-center">
            <Button
              ref={retryRef}
              onClick={reset}
              className="font-display"
            >
              <RefreshCw className="size-4 mr-2" />
              Try Again
            </Button>
            <Button
              onClick={() => window.location.href = '/'}
              variant="outline"
              className="font-display"
            >
              Go Home
            </Button>
          </div>

          <div className="text-center">
            <p className="text-xs text-[var(--tls-fg-subtle)]">
              If this keeps happening, reload once more and then try a different route into the page.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
